import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from '../hooks/useUsers';
import { usePerformers } from '../hooks/usePerformers';
import { usePerformerTiers, useUpdatePerformerTiers } from '../hooks/usePerformerTiers';
import { useAdminEventNotes, useUpdatePerformerNotes } from '../hooks/useNotes';
import { useAllEvents } from '../hooks/useInventory';
import { useActivityLog } from '../hooks/useActivityLog';
import { useStats } from '../hooks/useStats';
import { formatDateTimeEastern, formatEventDateOnly } from '../utils/dates';
import { isSuperAdmin } from '../utils/roles';
import AiAdminTab from '../components/AiAdminTab';
import NoteSyncAdminTab from '../components/NoteSyncAdminTab';
import TicketMetricAdminTab from '../components/TicketMetricAdminTab';
import DataAdminTab from '../components/DataAdminTab';
import TagsAdminTab from '../components/TagsAdminTab';
import { SkyboxSessionChip, SkyboxSessionPanel } from '../components/SkyboxSessionPanel';
import { useSkyboxSessions } from '../hooks/useSkyboxSessions';

// Human labels for activity_log.action (price rows are the historical default)
const ACTION_LABELS = {
  price_update: 'price',
  note_update: 'event note',
  note_delete: 'event note deleted',
  public_note_auto: 'public note (auto)',
  public_note_update: 'public note',
  note_request_create: 'note request',
  note_request_link: 'note link',
  note_request_resolve: 'note resolve',
  note_request_cancel: 'note cancel',
  note_sync_config_update: 'note sync config',
  note_category_update: 'note categories',
  dataset_job_pause: 'backfill pause',
  dataset_job_resume: 'backfill resume',
  dataset_job_run: 'backfill run',
  dataset_job_update: 'backfill settings',
  dataset_session_set: 'backfill login',
  dataset_slot_cap_set: 'backfill cap',
  dataset_auto_session_set: 'Lion Auto login',
  // Lion Auto's scheduled runner. Its price writes (source lion_auto_auto)
  // are the server's, not a person's.
  lion_auto_auto: 'Lion Auto (automatic)',
  lion_auto_auto_run: 'Lion Auto: runner run',
  lion_auto_ladder: 'Lion Auto: ladder apply',
  lion_auto_group_create: 'Lion Auto: group created',
  lion_auto_group_update: 'Lion Auto: group updated',
  lion_auto_group_delete: 'Lion Auto: group deleted',
  lion_auto_group_approve: 'Lion Auto: listings approved',
  lion_auto_group_dismiss: 'Lion Auto: listings dismissed',
  lion_auto_checker_import: 'Lion Auto: checker import',
  lion_auto_group_auto_on: 'Lion Auto: Auto on',
  lion_auto_group_auto_off: 'Lion Auto: Auto off',
  lion_auto_auto_config: 'Lion Auto: runner settings',
  lion_auto_discord_config: 'Lion Auto: Discord webhook',
  lion_auto_discord_test: 'Lion Auto: Discord test',
  lion_auto_floor_set: 'Lion Auto: floor',
  lion_auto_floor_follow: 'Lion Auto: groups follow floor',
  lion_auto_floor_reprice_now: 'Lion Auto: re-price now',
  lion_auto_sale_refill_set: 'Lion Auto: reprice after a sale',
  lion_auto_template_save: 'Lion Auto: template saved',
  lion_auto_template_update: 'Lion Auto: template updated',
  lion_auto_template_delete: 'Lion Auto: template deleted',
  lion_auto_template_apply: 'Lion Auto: template applied',
  note_request_attachment_add: 'note request photo added',
  note_request_attachment_delete: 'note request photo deleted',
  merge_listings: 'listings merged',
  split_listings: 'listings split',
  ai_initial_price: 'AI: initial price',
  ai_analyze: 'AI: analyze',
  ai_ask: 'AI: ask',
  ai_dismiss: 'AI: dismiss',
  ai_rate: 'AI: rate',
  ai_config_update: 'AI: settings',
  tm_link: 'TicketMetric link',
  tm_unlink: 'TicketMetric unlink',
  ticketmetric_config_update: 'TicketMetric settings',
  ticketmetric_test: 'TicketMetric test',
  skybox_credentials_update: 'Skybox credentials',
  skybox_credentials_reveal: 'Skybox credentials revealed',
  skybox_session_set: 'Skybox session set',
  skybox_session_clear: 'Skybox session cleared',
  // Admin → Tags Buyer box; the tag is the row's event_name
  buyer_tag_on: 'Buyer tag on',
  buyer_tag_off: 'Buyer tag off',
};

// Action dropdown for the activity filter, A-Z by label
const ACTION_FILTER_OPTIONS = Object.entries(ACTION_LABELS)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Rolling date-assignment windows — keys must match backend/utils/dateWindows.js
const DATE_WINDOWS = [
  { key: 'w1', label: '0–1 week out', hint: 'today – 7 days', short: '0–1w' },
  { key: 'w2', label: '1 week–1 month out', hint: '8 – 30 days', short: '1w–1m' },
  { key: 'w3', label: '1 month+ out', hint: '31+ days', short: '1m+' },
];

const EMPTY_DATE_ASSIGNMENT = { isActive: false, mode: 'rolling', windows: [], startDate: '', endDate: '' };

// ?tab=<key> opens a tab directly (Compare links to ?tab=ticketmetric)
const ADMIN_TABS = ['users', 'performers', 'activity', 'stats', 'tags', 'ai', 'ticketmetric', 'data', 'notesync'];
const SUPER_ADMIN_TABS = new Set(['users', 'activity', 'stats', 'ai', 'ticketmetric', 'data']);
const EMPTY_ACTIVITY_FILTERS = { userId: '', action: '', event: '', eventDate: '', dateFrom: '', dateTo: '' };

function Admin({ user, onLogout }) {
  // Users, Activity Log, Stats and AI are super-admin-only; plain admins get
  // the Performers/Tags/Note Sync tabs (backend enforces the same split).
  const superAdmin = isSuperAdmin(user.role);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const wanted = searchParams.get('tab');
    if (wanted && ADMIN_TABS.includes(wanted) && (superAdmin || !SUPER_ADMIN_TABS.has(wanted))) return wanted;
    return superAdmin ? 'users' : 'performers';
  });
  const [activityFilters, setActivityFilters] = useState(EMPTY_ACTIVITY_FILTERS);
  const [appliedActivityFilters, setAppliedActivityFilters] = useState(EMPTY_ACTIVITY_FILTERS);
  const [activityPage, setActivityPage] = useState(0);
  const ACTIVITY_PAGE_SIZE = 50;
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [artistSearch, setArtistSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);

  // Performers tab state
  const [performerSearch, setPerformerSearch] = useState('');

  const [tierFilter, setTierFilter] = useState('all');
  const [tierChanges, setTierChanges] = useState({});
  const [notesChanges, setNotesChanges] = useState({});
  const [expandedPerformer, setExpandedPerformer] = useState(null);
  const [showPastEventNotes, setShowPastEventNotes] = useState(false);
  const [performerSort, setPerformerSort] = useState({ field: 'performerName', dir: 'asc' });
  const [selectedPerformers, setSelectedPerformers] = useState(new Set());
  const [bulkTier, setBulkTier] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    displayName: '',
    role: 'pricer',
    canSeeCost: true,
    maxPriceChangePercent: 50,
    maxPriceIncreasePercent: 300,
    minPrice: 1,
    maxPrice: 100000,
    assignedArtists: [],
    assignedTiers: [],
    dateAssignment: { ...EMPTY_DATE_ASSIGNMENT }
  });

  const navigate = useNavigate();

  // Data hooks
  const {
    data: users = [],
    isLoading: usersLoading,
    error: usersError,
  } = useUsers({ enabled: superAdmin });

  // Per-pricer Skybox sessions (chip in the users table, panel in the edit modal)
  const { data: skyboxSessionsData } = useSkyboxSessions({ enabled: superAdmin });
  const skyboxSessionsById = useMemo(() => {
    const map = {};
    for (const s of skyboxSessionsData?.sessions || []) map[s.userId] = s;
    return map;
  }, [skyboxSessionsData]);
  const flash = (msg, isError) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 5000); }
    else { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); }
  };

  const {
    data: performers = [],
    isLoading: performersLoading,
  } = usePerformers();

  const {
    data: performerTiers = [],
    isLoading: performerTiersLoading,
  } = usePerformerTiers({
    enabled: activeTab === 'performers',
  });

  const updateTiersMutation = useUpdatePerformerTiers();
  const updateNotesMutation = useUpdatePerformerNotes();

  const { data: adminEventNotes = [] } = useAdminEventNotes({
    enabled: activeTab === 'performers',
  });

  const {
    data: allEvents = [],
    isLoading: eventsLoading,
  } = useAllEvents();

  const activityParams = {
    limit: ACTIVITY_PAGE_SIZE,
    offset: activityPage * ACTIVITY_PAGE_SIZE,
    ...(appliedActivityFilters.userId && { userId: appliedActivityFilters.userId }),
    ...(appliedActivityFilters.action && { action: appliedActivityFilters.action }),
    ...(appliedActivityFilters.event.trim() && { event: appliedActivityFilters.event.trim() }),
    ...(appliedActivityFilters.eventDate && { eventDate: appliedActivityFilters.eventDate }),
    ...(appliedActivityFilters.dateFrom && { dateFrom: appliedActivityFilters.dateFrom }),
    ...(appliedActivityFilters.dateTo && { dateTo: appliedActivityFilters.dateTo }),
  };
  const {
    data: activityData,
    isLoading: activityLoading,
  } = useActivityLog(activityParams, {
    enabled: superAdmin && activeTab === 'activity',
  });
  const activityLog = activityData?.logs || [];
  const activityTotal = activityData?.total || 0;

  const {
    data: stats = null,
    isLoading: statsLoading,
  } = useStats({
    enabled: superAdmin && activeTab === 'stats',
  });

  // Mutations
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  // Derived loading state per tab
  const loading =
    (activeTab === 'users' && (usersLoading || performersLoading || eventsLoading)) ||
    (activeTab === 'performers' && performerTiersLoading) ||
    (activeTab === 'activity' && (usersLoading || activityLoading)) ||
    (activeTab === 'stats' && statsLoading);

  const handleActivityFilterChange = (field, value) => {
    setActivityFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleActivitySearch = (e) => {
    e.preventDefault();
    setActivityPage(0);
    setAppliedActivityFilters(activityFilters);
  };

  const clearActivityFilters = () => {
    setActivityFilters(EMPTY_ACTIVITY_FILTERS);
    setAppliedActivityFilters(EMPTY_ACTIVITY_FILTERS);
    setActivityPage(0);
  };

  const handleActivityPageChange = (newPage) => {
    setActivityPage(newPage);
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      displayName: '',
      role: 'pricer',
      canSeeCost: true,
      maxPriceChangePercent: 50,
      maxPriceIncreasePercent: 300,
      minPrice: 1,
      maxPrice: 100000,
      assignedArtists: [],
      assignedTiers: [],
      dateAssignment: { ...EMPTY_DATE_ASSIGNMENT }
    });
    setShowModal(true);
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      username: userToEdit.username,
      password: '',
      displayName: userToEdit.displayName,
      role: userToEdit.role,
      canSeeCost: userToEdit.canSeeCost,
      maxPriceChangePercent: userToEdit.maxPriceChangePercent,
      maxPriceIncreasePercent: userToEdit.maxPriceIncreasePercent || 300,
      minPrice: userToEdit.minPrice,
      maxPrice: userToEdit.maxPrice,
      assignedArtists: userToEdit.assignedArtists || [],
      assignedTiers: userToEdit.assignedTiers || [],
      dateAssignment: userToEdit.dateAssignment
        ? { ...userToEdit.dateAssignment, windows: userToEdit.dateAssignment.windows || [] }
        : { ...EMPTY_DATE_ASSIGNMENT }
    });
    setShowModal(true);
  };

  const handleSaveUser = async () => {
    const da = formData.dateAssignment;
    if (formData.role === 'pricer' && da.isActive) {
      if (da.mode === 'rolling' && da.windows.length === 0) {
        setError('Select at least one date window or disable the date assignment');
        setTimeout(() => setError(''), 5000);
        return;
      }
      if (da.mode === 'custom' && (!da.startDate || !da.endDate || da.startDate > da.endDate)) {
        setError('Custom date range needs a start date on or before the end date');
        setTimeout(() => setError(''), 5000);
        return;
      }
    }

    // Date assignments only apply to pricers; omit the field for other roles
    // so a stale form value can't fail server validation over hidden controls
    const userData = formData.role === 'pricer'
      ? formData
      : formData.role === 'buyer'
        // Buyers never see cost; the checkbox is hidden for them so don't send a stale value
        ? { ...formData, dateAssignment: undefined, canSeeCost: false }
        : { ...formData, dateAssignment: undefined };

    try {
      if (editingUser) {
        await updateUserMutation.mutateAsync({ id: editingUser.id, userData });
        setSuccessMessage('User updated successfully');
      } else {
        await createUserMutation.mutateAsync(userData);
        setSuccessMessage('User created successfully');
      }

      setShowModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await deleteUserMutation.mutateAsync(userId);
      setSuccessMessage('User deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const toggleArtist = (artistName) => {
    setFormData(prev => ({
      ...prev,
      assignedArtists: prev.assignedArtists.includes(artistName)
        ? prev.assignedArtists.filter(a => a !== artistName)
        : [...prev.assignedArtists, artistName]
    }));
  };

  // Performer tier handlers
  const handleTierChange = (performerName, newTier) => {
    setTierChanges(prev => ({ ...prev, [performerName]: newTier }));
  };

  // Stage a general-notes edit (committed onBlur). A value matching the server
  // is unstaged entirely, so an untouched blur or an edit reverted back to the
  // original never counts as a change (or re-saves and re-attributes the note).
  const handleNotesChange = (performerName, value, serverValue) => {
    setNotesChanges(prev => {
      if (value === (serverValue || '')) {
        if (prev[performerName] === undefined) return prev;
        const { [performerName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [performerName]: value };
    });
  };

  const getEffectiveNotes = (performer) => {
    if (notesChanges[performer.performerName] !== undefined) {
      return notesChanges[performer.performerName];
    }
    return performer.generalNotes || '';
  };

  const handleSaveTiers = async () => {
    // Tier assignments and notes ship on separate endpoints: the tier route
    // DELETEs rows for falsy tiers, so notes must never ride its payload
    const assignments = Object.entries(tierChanges).map(([performerName, tier]) => ({
      performerName,
      tier: tier || null
    }));
    const notesPayload = { ...notesChanges };
    const totalChanges = assignments.length + Object.keys(notesPayload).length;

    if (totalChanges === 0) return;

    try {
      if (assignments.length > 0) {
        await updateTiersMutation.mutateAsync(assignments);
      }
      if (Object.keys(notesPayload).length > 0) {
        await updateNotesMutation.mutateAsync(notesPayload);
      }
      setTierChanges({});
      setNotesChanges({});
      setSelectedPerformers(new Set());
      setSuccessMessage(`Updated ${totalChanges} performer change(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save performer changes');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Same URL Dashboard's openPricingTool builds
  const openCompareForEvent = (event) => {
    const params = new URLSearchParams();
    params.set('name', event.eventName || 'Event');
    if (event.eventDate) params.set('date', event.eventDate);
    if (event.venue) params.set('venue', event.venue);
    if (event.venueCity) params.set('city', event.venueCity);
    if (event.venueState) params.set('state', event.venueState);
    if (event.vividSeatsEventUrl) params.set('vividUrl', event.vividSeatsEventUrl);
    if (event.chartUrl) params.set('chartUrl', event.chartUrl);
    window.open(`/compare/${event.eventId}?${params.toString()}`, '_blank');
  };

  const togglePerformerSelection = (performerName) => {
    setSelectedPerformers(prev => {
      const next = new Set(prev);
      if (next.has(performerName)) {
        next.delete(performerName);
      } else {
        next.add(performerName);
      }
      return next;
    });
  };

  const toggleSelectAll = (visiblePerformers) => {
    const allVisible = visiblePerformers.map(p => p.performerName);
    const allSelected = allVisible.every(name => selectedPerformers.has(name));
    if (allSelected) {
      setSelectedPerformers(new Set());
    } else {
      setSelectedPerformers(new Set(allVisible));
    }
  };

  const applyBulkTier = () => {
    if (selectedPerformers.size === 0) return;
    const newChanges = { ...tierChanges };
    for (const name of selectedPerformers) {
      newChanges[name] = bulkTier;
    }
    setTierChanges(newChanges);
    setSelectedPerformers(new Set());
    setBulkTier('');
  };

  const getEffectiveTier = (performer) => {
    if (tierChanges[performer.performerName] !== undefined) {
      return tierChanges[performer.performerName];
    }
    return performer.tier || '';
  };

  const sortedPerformers = useMemo(() => {
    let filtered = performerTiers;

    // Search filter
    if (performerSearch) {
      const search = performerSearch.toLowerCase();
      filtered = filtered.filter(p =>
        p.performerName.toLowerCase().includes(search)
      );
    }

    // Tier filter
    if (tierFilter !== 'all') {
      if (tierFilter === 'untiered') {
        filtered = filtered.filter(p => !getEffectiveTier(p));
      } else {
        filtered = filtered.filter(p => getEffectiveTier(p) === tierFilter);
      }
    }

    // Sort
    const { field, dir } = performerSort;
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      if (field === 'tier') {
        aVal = getEffectiveTier(a) || 'zzz';
        bVal = getEffectiveTier(b) || 'zzz';
      }
      if (typeof aVal === 'string') {
        return dir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return dir === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    });

    return filtered;
  }, [performerTiers, performerSearch, tierFilter, performerSort, tierChanges]);

  const handlePerformerSort = (field) => {
    setPerformerSort(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortArrow = (field) => {
    if (performerSort.field !== field) return '';
    return performerSort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const tierStats = useMemo(() => performerTiers.reduce((acc, p) => {
    const tier = getEffectiveTier(p) || 'untiered';
    if (!acc[tier]) {
      acc[tier] = { count: 0, tickets: 0, events: 0, cost: 0 };
    }
    acc[tier].count += 1;
    acc[tier].tickets += p.ticketCount || 0;
    acc[tier].events += p.eventCount || 0;
    acc[tier].cost += p.totalCost || 0;
    return acc;
  }, { S: { count: 0, tickets: 0, events: 0, cost: 0 }, '1': { count: 0, tickets: 0, events: 0, cost: 0 }, '2': { count: 0, tickets: 0, events: 0, cost: 0 }, '3': { count: 0, tickets: 0, events: 0, cost: 0 }, Broadway: { count: 0, tickets: 0, events: 0, cost: 0 }, Presales: { count: 0, tickets: 0, events: 0, cost: 0 }, Parking: { count: 0, tickets: 0, events: 0, cost: 0 }, untiered: { count: 0, tickets: 0, events: 0, cost: 0 } }),
  [performerTiers, tierChanges]);

  const formatDate = (dateStr) => formatDateTimeEastern(dateStr, { second: '2-digit' });

  return (
    <div className="app-container">
      <nav className="navbar">
        <div className="navbar-brand">Pricing Portal - Admin</div>
        <div className="navbar-user">
          <span>{user.displayName}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/')}>
            Dashboard
          </button>
          {superAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/reports')}>
              Reports
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/orders')}>
            Note Requests
          </button>
          <button className="btn btn-secondary btn-sm" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>

      <main className="main-content">
        {(error || usersError) && (
          <div className="message message-error">
            {error || usersError?.message || 'Failed to load data'}
          </div>
        )}
        {successMessage && <div className="message message-success">{successMessage}</div>}

        <div className="tabs">
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
          )}
          <button
            className={`tab ${activeTab === 'performers' ? 'active' : ''}`}
            onClick={() => setActiveTab('performers')}
          >
            Performers
          </button>
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              Activity Log
            </button>
          )}
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => setActiveTab('stats')}
            >
              Stats
            </button>
          )}
          <button
            className={`tab ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            Tags
          </button>
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'ai' ? 'active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              AI
            </button>
          )}
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'ticketmetric' ? 'active' : ''}`}
              onClick={() => setActiveTab('ticketmetric')}
            >
              TicketMetric
            </button>
          )}
          {superAdmin && (
            <button
              className={`tab ${activeTab === 'data' ? 'active' : ''}`}
              onClick={() => setActiveTab('data')}
            >
              Data
            </button>
          )}
          <button
            className={`tab ${activeTab === 'notesync' ? 'active' : ''}`}
            onClick={() => setActiveTab('notesync')}
          >
            Note Sync
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {superAdmin && activeTab === 'ai' && (
              <AiAdminTab
                onMessage={(msg, isError) => {
                  if (isError) { setError(msg); setTimeout(() => setError(''), 5000); }
                  else { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); }
                }}
              />
            )}
            {superAdmin && activeTab === 'ticketmetric' && (
              <TicketMetricAdminTab
                onMessage={(msg, isError) => {
                  if (isError) { setError(msg); setTimeout(() => setError(''), 5000); }
                  else { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); }
                }}
              />
            )}
            {superAdmin && activeTab === 'data' && (
              <DataAdminTab
                onMessage={(msg, isError) => {
                  if (isError) { setError(msg); setTimeout(() => setError(''), 5000); }
                  else { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); }
                }}
              />
            )}
            {activeTab === 'notesync' && (
              <NoteSyncAdminTab
                onMessage={(msg, isError) => {
                  if (isError) { setError(msg); setTimeout(() => setError(''), 5000); }
                  else { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 3000); }
                }}
              />
            )}
            {superAdmin && activeTab === 'users' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Manage Users</h2>
                  <button className="btn btn-primary" onClick={handleCreateUser}>
                    + Add User
                  </button>
                </div>

                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Display Name</th>
                        <th>Role</th>
                        <th>Can See Cost</th>
                        <th>Price Limits</th>
                        <th>Tiers</th>
                        <th>Assigned Artists</th>
                        <th>Status</th>
                        <th>Skybox</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => {
                        // Get events that match this user's assigned artists
                        const assignedEvents = u.role === 'pricer' && u.assignedArtists?.length > 0
                          ? allEvents.filter(event =>
                              u.assignedArtists.some(artist =>
                                event.performer?.toLowerCase().includes(artist.toLowerCase()) ||
                                event.eventName?.toLowerCase().includes(artist.toLowerCase())
                              )
                            )
                          : [];

                        return (
                          <React.Fragment key={u.id}>
                            <tr>
                              <td>{u.username}</td>
                              <td>{u.displayName}</td>
                              <td>
                                <span className={`tag tag-${u.role}`}>{u.role}</span>
                              </td>
                              <td>{u.canSeeCost ? 'Yes' : 'No'}</td>
                              <td style={{ fontSize: '0.75rem' }}>
                                {u.role === 'pricer' && (
                                  <>
                                    <span style={{ color: '#ff6b6b' }}>-{u.maxPriceChangePercent}%</span> / <span style={{ color: '#51cf66' }}>+{u.maxPriceIncreasePercent || 300}%</span><br />
                                    ${u.minPrice} - ${u.maxPrice}
                                  </>
                                )}
                              </td>
                              <td>
                                {u.assignedTiers?.length > 0 || u.dateAssignment?.isActive ? (
                                  <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                    {u.assignedTiers?.map(t => (
                                      <span key={t} className={`tier-badge tier-${t.toLowerCase()}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }}>
                                        {t}
                                      </span>
                                    ))}
                                    {u.dateAssignment?.isActive && (
                                      <span style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', borderRadius: '3px', background: '#1a3d1f', border: '1px solid #27ae60', color: '#2ecc71' }}>
                                        {u.dateAssignment.mode === 'rolling'
                                          ? (u.dateAssignment.windows || [])
                                              .map(k => DATE_WINDOWS.find(w => w.key === k)?.short)
                                              .filter(Boolean).join(', ') || 'No windows'
                                          : `${u.dateAssignment.startDate} – ${u.dateAssignment.endDate}`}
                                      </span>
                                    )}
                                  </div>
                                ) : '-'}
                              </td>
                              <td style={{ maxWidth: '200px', fontSize: '0.75rem' }}>
                                {u.assignedArtists?.length > 0 ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span>{u.assignedArtists.join(', ')}</span>
                                    {assignedEvents.length > 0 && (
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                                        style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem' }}
                                      >
                                        {assignedEvents.length} events {expandedUser === u.id ? '▲' : '▼'}
                                      </button>
                                    )}
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                <span className={`tag ${u.isActive ? 'tag-pricer' : 'tag-inactive'}`}>
                                  {u.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                {u.role !== 'buyer' && <SkyboxSessionChip session={skyboxSessionsById[u.id]} />}
                              </td>
                              <td>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleEditUser(u)}
                                  style={{ marginRight: '0.5rem' }}
                                >
                                  Edit
                                </button>
                                {u.id !== user.id && (
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDeleteUser(u.id)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedUser === u.id && assignedEvents.length > 0 && (
                              <tr>
                                <td colSpan="10" style={{ background: '#1a1a2e', padding: '1rem' }}>
                                  <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                    Assigned Events ({assignedEvents.length}):
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.5rem' }}>
                                    {assignedEvents.map(event => (
                                      <div
                                        key={event.eventId}
                                        style={{
                                          background: '#252540',
                                          padding: '0.5rem',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        <div style={{ fontWeight: 'bold' }}>{event.eventName}</div>
                                        <div style={{ color: '#a0a0a0' }}>
                                          {formatEventDateOnly(event.eventDate, { weekday: 'short' })} • {event.venue}
                                        </div>
                                        <div style={{ color: '#51cf66' }}>
                                          {event.inventory?.length || 0} listings
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'performers' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Performer Tiers</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {(Object.keys(tierChanges).length + Object.keys(notesChanges).length) > 0 && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveTiers}
                        disabled={updateTiersMutation.isPending || updateNotesMutation.isPending}
                      >
                        {(updateTiersMutation.isPending || updateNotesMutation.isPending)
                          ? 'Saving...'
                          : `Save Changes (${Object.keys(tierChanges).length + Object.keys(notesChanges).length})`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Tier summary */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  {[
                    { key: 'S', label: 'S', cls: 'tier-s' },
                    { key: '1', label: 'Tier 1', cls: 'tier-1' },
                    { key: '2', label: 'Tier 2', cls: 'tier-2' },
                    { key: '3', label: 'Tier 3', cls: 'tier-3' },
                    { key: 'Broadway', label: 'Broadway', cls: 'tier-broadway' },
                    { key: 'Presales', label: 'Presales', cls: 'tier-presales' },
                    { key: 'Parking', label: 'Parking', cls: 'tier-parking' },
                    { key: 'untiered', label: 'Untiered', cls: 'tier-none' },
                  ].map(t => (
                    <div
                      key={t.key}
                      className={`tier-badge ${t.cls}`}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', lineHeight: '1.4', cursor: 'pointer', opacity: tierFilter === t.key || tierFilter === 'all' ? 1 : 0.5 }}
                      onClick={() => setTierFilter(tierFilter === t.key ? 'all' : t.key)}
                    >
                      <div style={{ fontWeight: 'bold' }}>{t.label}: {tierStats[t.key]?.count || 0}</div>
                      <div style={{ fontSize: '0.65rem', opacity: 0.85 }}>
                        {tierStats[t.key]?.tickets || 0} tix &middot; {tierStats[t.key]?.events || 0} events &middot; ${(tierStats[t.key]?.cost || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Search and filter */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search performers..."
                      value={performerSearch}
                      onChange={e => setPerformerSearch(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <select
                      className="form-input"
                      value={tierFilter}
                      onChange={e => setTierFilter(e.target.value)}
                    >
                      <option value="all">All Tiers</option>
                      <option value="S">Tier S</option>
                      <option value="1">Tier 1</option>
                      <option value="2">Tier 2</option>
                      <option value="3">Tier 3</option>
                      <option value="Broadway">Broadway</option>
                      <option value="Presales">Presales</option>
                      <option value="Parking">Parking</option>
                      <option value="untiered">Untiered</option>
                    </select>
                  </div>
                </div>

                {/* Bulk action bar */}
                {selectedPerformers.size > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    marginBottom: '0.75rem', padding: '0.6rem 1rem',
                    background: '#1a1a2e', borderRadius: '6px', border: '1px solid #3d3d5c'
                  }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                      {selectedPerformers.size} selected
                    </span>
                    <select
                      className="form-input"
                      value={bulkTier}
                      onChange={e => setBulkTier(e.target.value)}
                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem', width: '120px' }}
                    >
                      <option value="">Remove Tier</option>
                      <option value="S">Tier S</option>
                      <option value="1">Tier 1</option>
                      <option value="2">Tier 2</option>
                      <option value="3">Tier 3</option>
                      <option value="Broadway">Broadway</option>
                      <option value="Presales">Presales</option>
                      <option value="Parking">Parking</option>
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={applyBulkTier}>
                      Apply
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedPerformers(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                )}

                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={sortedPerformers.length > 0 && sortedPerformers.every(p => selectedPerformers.has(p.performerName))}
                            onChange={() => toggleSelectAll(sortedPerformers)}
                          />
                        </th>
                        <th
                          style={{ cursor: 'pointer' }}
                          onClick={() => handlePerformerSort('performerName')}
                        >
                          Performer{sortArrow('performerName')}
                        </th>
                        <th>General Notes</th>
                        <th
                          style={{ cursor: 'pointer' }}
                          onClick={() => handlePerformerSort('tier')}
                        >
                          Tier{sortArrow('tier')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', textAlign: 'right' }}
                          onClick={() => handlePerformerSort('ticketCount')}
                        >
                          Tickets{sortArrow('ticketCount')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', textAlign: 'right' }}
                          onClick={() => handlePerformerSort('eventCount')}
                        >
                          Events{sortArrow('eventCount')}
                        </th>
                        <th
                          style={{ cursor: 'pointer', textAlign: 'right' }}
                          onClick={() => handlePerformerSort('totalCost')}
                        >
                          Total Cost{sortArrow('totalCost')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPerformers.length === 0 ? (
                        <tr>
                          <td colSpan="7" style={{ textAlign: 'center', color: '#a0a0a0' }}>
                            No performers found
                          </td>
                        </tr>
                      ) : (
                        sortedPerformers.map(p => {
                          const currentTier = getEffectiveTier(p);
                          const isChanged = tierChanges[p.performerName] !== undefined || notesChanges[p.performerName] !== undefined;
                          const isSelected = selectedPerformers.has(p.performerName);
                          const isExpanded = expandedPerformer === p.performerName;

                          let performerEvents = [];
                          let pastNotes = [];
                          if (isExpanded) {
                            const noteByEventId = new Map(adminEventNotes.map(n => [String(n.eventId), n]));
                            // Exact match (substring matching would list "Museum Tour"
                            // under "Muse"); allEvents is already sorted soonest-first
                            performerEvents = allEvents
                              .filter(e => e.performer === p.performerName)
                              .map(e => ({ ...e, note: noteByEventId.get(String(e.eventId)) }));
                            const liveIds = new Set(performerEvents.map(e => String(e.eventId)));
                            pastNotes = adminEventNotes.filter(n =>
                              n.performerName === p.performerName && !liveIds.has(String(n.eventId))
                            );
                          }

                          return (
                            <React.Fragment key={p.performerName}>
                            <tr
                              style={isChanged ? { background: '#1a2a1a' } : isSelected ? { background: '#1a1a2e' } : {}}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => togglePerformerSelection(p.performerName)}
                                />
                              </td>
                              <td
                                style={{ cursor: 'pointer' }}
                                onClick={() => setExpandedPerformer(isExpanded ? null : p.performerName)}
                                title="Show this performer's events"
                              >
                                <div style={{ fontWeight: 'bold' }}>
                                  {p.performerName}{' '}
                                  <span style={{ color: '#a0a0a0', fontSize: '0.7rem' }}>{isExpanded ? '▲' : '▼'}</span>
                                </div>
                                {p.category && (
                                  <div style={{ fontSize: '0.7rem', color: '#a0a0a0' }}>{p.category}</div>
                                )}
                                {p.inInventory === false && (
                                  <div style={{ fontSize: '0.65rem', color: '#e8a09a' }}>no longer in inventory</div>
                                )}
                              </td>
                              <td>
                                <textarea
                                  key={`notes-${p.performerName}-${p.generalNotes || ''}`}
                                  className="form-input"
                                  rows={2}
                                  defaultValue={getEffectiveNotes(p)}
                                  onBlur={e => handleNotesChange(p.performerName, e.target.value, p.generalNotes)}
                                  placeholder="General notes..."
                                  maxLength={5000}
                                  style={{ width: '220px', fontSize: '0.8rem', resize: 'vertical', padding: '0.25rem 0.5rem' }}
                                />
                              </td>
                              <td>
                                <select
                                  className="form-input"
                                  value={currentTier}
                                  onChange={e => handleTierChange(p.performerName, e.target.value)}
                                  style={{
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.85rem',
                                    width: '80px',
                                    background: currentTier === 'S' ? '#2d1f3d' :
                                                currentTier === '1' ? '#1f2d3d' :
                                                currentTier === '2' ? '#1f3d2d' :
                                                currentTier === '3' ? '#3d2d1f' :
                                                currentTier === 'Broadway' ? '#3d1f1f' :
                                                currentTier === 'Presales' ? '#1f3d3a' :
                                                currentTier === 'Parking' ? '#2d2d1f' : ''
                                  }}
                                >
                                  <option value="">-</option>
                                  <option value="S">S</option>
                                  <option value="1">1</option>
                                  <option value="2">2</option>
                                  <option value="3">3</option>
                                  <option value="Broadway">BW</option>
                                  <option value="Presales">PS</option>
                                  <option value="Parking">PK</option>
                                </select>
                              </td>
                              <td style={{ textAlign: 'right' }}>{p.ticketCount}</td>
                              <td style={{ textAlign: 'right' }}>{p.eventCount}</td>
                              <td style={{ textAlign: 'right' }}>
                                ${p.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan="7" style={{ background: '#1a1a2e', padding: '1rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                                      Events ({performerEvents.length})
                                    </span>
                                    {pastNotes.length > 0 && (
                                      <button
                                        className="btn btn-secondary btn-sm"
                                        style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem' }}
                                        onClick={() => setShowPastEventNotes(!showPastEventNotes)}
                                      >
                                        {showPastEventNotes ? 'Hide' : 'Show'} past events with notes ({pastNotes.length})
                                      </button>
                                    )}
                                  </div>
                                  {performerEvents.length === 0 && pastNotes.length === 0 && (
                                    <div style={{ color: '#a0a0a0', fontSize: '0.75rem' }}>
                                      No events in inventory for this performer
                                    </div>
                                  )}
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.5rem' }}>
                                    {performerEvents.map(event => (
                                      <div
                                        key={event.eventId}
                                        style={{ background: '#252540', padding: '0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
                                          <div style={{ fontWeight: 'bold' }}>{event.eventName}</div>
                                          <button
                                            className="btn btn-secondary btn-sm"
                                            style={{ padding: '0.15rem 0.4rem', fontSize: '0.7rem', flexShrink: 0 }}
                                            onClick={() => openCompareForEvent(event)}
                                            title="Open this event's Compare page"
                                          >
                                            Open
                                          </button>
                                        </div>
                                        <div style={{ color: '#a0a0a0' }}>
                                          {formatEventDateOnly(event.eventDate, { weekday: 'short' })} • {event.venue}
                                        </div>
                                        <div style={{ marginTop: '0.35rem', whiteSpace: 'pre-wrap' }}>
                                          {event.note?.notes
                                            ? event.note.notes
                                            : <span style={{ color: '#a0a0a0', fontStyle: 'italic' }}>No notes</span>}
                                        </div>
                                        {event.note?.updatedBy && (
                                          <div style={{ color: '#a0a0a0', fontSize: '0.65rem', marginTop: '0.25rem' }}>
                                            Last updated by {event.note.updatedBy}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                    {showPastEventNotes && pastNotes.map(note => (
                                      <div
                                        key={`past-${note.eventId}`}
                                        style={{ background: '#252540', padding: '0.5rem', borderRadius: '4px', fontSize: '0.75rem', opacity: 0.75 }}
                                      >
                                        <div style={{ fontWeight: 'bold' }}>
                                          {note.eventName || `Event ${note.eventId}`}{' '}
                                          <span style={{ color: '#e8a09a', fontWeight: 'normal', fontSize: '0.65rem' }}>
                                            (no longer in inventory)
                                          </span>
                                        </div>
                                        <div style={{ color: '#a0a0a0' }}>
                                          {note.eventDate ? formatEventDateOnly(note.eventDate, { weekday: 'short' }) : 'Unknown date'}
                                          {note.venue ? ` • ${note.venue}` : ''}
                                        </div>
                                        <div style={{ marginTop: '0.35rem', whiteSpace: 'pre-wrap' }}>{note.notes}</div>
                                        {note.updatedBy && (
                                          <div style={{ color: '#a0a0a0', fontSize: '0.65rem', marginTop: '0.25rem' }}>
                                            Last updated by {note.updatedBy}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: '0.75rem', color: '#a0a0a0', fontSize: '0.8rem' }}>
                  Showing {sortedPerformers.length} of {performerTiers.length} performers
                </div>
              </div>
            )}

            {superAdmin && activeTab === 'activity' && (
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">Activity Log</h2>
                  <span style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>
                    {activityTotal} total records
                  </span>
                </div>

                {/* Filters */}
                <form onSubmit={handleActivitySearch} style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }}>User</label>
                    <select
                      className="form-input"
                      value={activityFilters.userId}
                      onChange={(e) => handleActivityFilterChange('userId', e.target.value)}
                      style={{ minWidth: '150px' }}
                    >
                      <option value="">All Users</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.displayName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Action</label>
                    <select
                      className="form-input"
                      value={activityFilters.action}
                      onChange={(e) => handleActivityFilterChange('action', e.target.value)}
                      style={{ minWidth: '180px' }}
                    >
                      <option value="">All Actions</option>
                      {ACTION_FILTER_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }} htmlFor="activity-event-search">Event</label>
                    <input
                      id="activity-event-search"
                      type="search"
                      className="form-input"
                      placeholder="Event name contains…"
                      maxLength={200}
                      value={activityFilters.event}
                      onChange={(e) => handleActivityFilterChange('event', e.target.value)}
                      style={{ minWidth: '200px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }} htmlFor="activity-event-date">Event Date</label>
                    <input
                      id="activity-event-date"
                      type="date"
                      className="form-input"
                      value={activityFilters.eventDate}
                      onChange={(e) => handleActivityFilterChange('eventDate', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Date From</label>
                    <input
                      type="date"
                      className="form-input"
                      value={activityFilters.dateFrom}
                      onChange={(e) => handleActivityFilterChange('dateFrom', e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.75rem' }}>Date To</label>
                    <input
                      type="date"
                      className="form-input"
                      value={activityFilters.dateTo}
                      onChange={(e) => handleActivityFilterChange('dateTo', e.target.value)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm">
                    Search
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={clearActivityFilters}>
                    Clear
                  </button>
                </form>

                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User</th>
                        <th title="The name Skybox's listing history shows for this change">Skybox actor</th>
                        <th>Action</th>
                        <th>Event</th>
                        <th>Event Date</th>
                        <th>Section/Row</th>
                        <th>Old Price</th>
                        <th>New Price</th>
                        <th>Change</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLog.length === 0 ? (
                        <tr>
                          <td colSpan="11" style={{ textAlign: 'center', color: '#a0a0a0' }}>
                            No activity found
                          </td>
                        </tr>
                      ) : (
                        activityLog.map(log => {
                          // Non-price actions (event notes, public-note sync) have null prices
                          const hasPrices = log.old_price != null && log.new_price != null;
                          const change = hasPrices ? log.new_price - log.old_price : null;
                          const percentChange = log.percentChange !== null && log.percentChange !== undefined
                            ? Number(log.percentChange).toFixed(1)
                            : (hasPrices && log.old_price > 0 ? ((change / log.old_price) * 100).toFixed(1) : '0');
                          const action = log.action || 'price_update';
                          const actionLabel = ACTION_LABELS[action] || action.replace(/_/g, ' ');
                          // Name Skybox's listing history shows for this change: an email is
                          // the pricer's own Skybox session, "Prime Lion Tickets" the API token;
                          // null for note actions and rows older than the column
                          const skyboxActor = log.skyboxActor || null;
                          const viaApiToken = skyboxActor === 'Prime Lion Tickets';

                          return (
                            <tr key={log.id}>
                              <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                              <td>{log.displayName || log.display_name}</td>
                              <td style={{ whiteSpace: 'nowrap', ...(viaApiToken ? { fontSize: '0.75rem', color: '#a0a0a0' } : {}) }}>
                                {skyboxActor || '—'}
                              </td>
                              <td>
                                <span className={`nr-chip nr-action ${action.startsWith('public_note') ? 'nr-action-note' : action.startsWith('note_') ? 'nr-action-req' : ''}`} title={action}>
                                  {actionLabel}
                                </span>
                              </td>
                              <td style={{ maxWidth: '200px' }}>{log.event_name || '—'}</td>
                              <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                {formatEventDateOnly(log.eventDate || log.event_date)}
                              </td>
                              <td>{log.section || log.row ? `${log.section ?? '—'} / ${log.row ?? '—'}` : (log.inventory_id ? `#${log.inventory_id}` : '—')}</td>
                              <td>{log.old_price != null ? `$${Number(log.old_price).toFixed(2)}` : '—'}</td>
                              <td>{log.new_price != null ? `$${Number(log.new_price).toFixed(2)}` : '—'}</td>
                              <td style={{ color: change == null ? '#a0a0a0' : change >= 0 ? '#51cf66' : '#ff6b6b', whiteSpace: 'nowrap' }}>
                                {change == null ? '—' : `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${percentChange}%)`}
                              </td>
                              <td style={{ fontSize: '0.7rem', color: '#a0a0a0' }}>
                                {log.ipAddress || log.ip_address || '-'}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {activityTotal > ACTIVITY_PAGE_SIZE && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleActivityPageChange(activityPage - 1)}
                      disabled={activityPage === 0}
                    >
                      Previous
                    </button>
                    <span style={{ color: '#a0a0a0', fontSize: '0.85rem' }}>
                      Page {activityPage + 1} of {Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE)}
                    </span>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleActivityPageChange(activityPage + 1)}
                      disabled={(activityPage + 1) * ACTIVITY_PAGE_SIZE >= activityTotal}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {superAdmin && activeTab === 'stats' && stats && (
              <>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalUsers}</div>
                    <div className="stat-label">Total Users</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.totalPricers}</div>
                    <div className="stat-label">Active Pricers</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.priceChangesToday}</div>
                    <div className="stat-label">Price Changes Today</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{stats.priceChangesThisWeek}</div>
                    <div className="stat-label">Price Changes This Week</div>
                  </div>
                </div>

                {stats.pricerBreakdown?.length > 0 && (
                  <div className="card" style={{ marginTop: '1.5rem' }}>
                    <div className="card-header">
                      <h2 className="card-title">Pricer Breakdown</h2>
                    </div>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Pricer</th>
                            <th>Events Repriced</th>
                            <th>Today</th>
                            <th>This Week</th>
                            <th>All Time</th>
                            <th>Last Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stats.pricerBreakdown.map(p => (
                            <tr key={p.userId}>
                              <td>
                                <div style={{ fontWeight: 'bold' }}>{p.displayName}</div>
                                <div style={{ fontSize: '0.7rem', color: '#a0a0a0' }}>{p.username}</div>
                              </td>
                              <td>
                                <span style={{ color: '#3b82f6', fontWeight: 'bold', fontSize: '1.1rem' }}>
                                  {p.uniqueEvents}
                                </span>
                              </td>
                              <td>
                                <span style={{ color: p.changesToday > 0 ? '#51cf66' : '#a0a0a0' }}>
                                  {p.changesToday}
                                </span>
                              </td>
                              <td>
                                <span style={{ color: p.changesThisWeek > 0 ? '#51cf66' : '#a0a0a0' }}>
                                  {p.changesThisWeek}
                                </span>
                              </td>
                              <td>{p.totalChanges}</td>
                              <td style={{ fontSize: '0.75rem', color: '#a0a0a0' }}>
                                {formatDateTimeEastern(p.lastActivity, { year: undefined })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'tags' && (
              <TagsAdminTab
                onMessage={(msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(''), 2000); }}
              />
            )}
          </>
        )}

        {/* User Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {editingUser ? 'Edit User' : 'Create User'}
                </h3>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  &times;
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.username}
                  onChange={e => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  disabled={!!editingUser}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Password {editingUser && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={formData.password}
                  onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Display Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.displayName}
                  onChange={e => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  className="form-input"
                  value={formData.role}
                  onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                >
                  <option value="pricer">Pricer</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="buyer">Buyer</option>
                </select>
                {formData.role === 'buyer' && (
                  <small style={{ color: '#a0a0a0', fontSize: '0.7rem', display: 'block', marginTop: '0.25rem' }}>
                    Buyers only see the Note Requests page (order number + note type). No inventory, pricing or cost access. 30-day login.
                  </small>
                )}
              </div>

              {formData.role !== 'buyer' && (
                <div className="form-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="canSeeCost"
                      checked={formData.canSeeCost}
                      onChange={e => setFormData(prev => ({ ...prev, canSeeCost: e.target.checked }))}
                    />
                    <label htmlFor="canSeeCost">Can See Cost</label>
                  </div>
                </div>
              )}

              {formData.role === 'pricer' && (
                <>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Max Decrease (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.maxPriceChangePercent}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          maxPriceChangePercent: parseFloat(e.target.value)
                        }))}
                        placeholder="50"
                      />
                      <small style={{ color: '#a0a0a0', fontSize: '0.7rem' }}>e.g., 50 = can decrease by up to 50%</small>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Max Increase (%)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.maxPriceIncreasePercent}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          maxPriceIncreasePercent: parseFloat(e.target.value)
                        }))}
                        placeholder="300"
                      />
                      <small style={{ color: '#a0a0a0', fontSize: '0.7rem' }}>e.g., 300 = can increase by up to 300%</small>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Min Price ($)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.minPrice}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          minPrice: parseFloat(e.target.value)
                        }))}
                      />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Max Price ($)</label>
                      <input
                        type="number"
                        className="form-input"
                        value={formData.maxPrice}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          maxPrice: parseFloat(e.target.value)
                        }))}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Assigned Tiers</label>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      {['S', '1', '2', '3', 'Broadway', 'Presales', 'Parking'].map(tier => {
                        const tierColors = { S: { bg: '#2d1f3d', border: '#9b59b6' }, '1': { bg: '#1f2d3d', border: '#3498db' }, '2': { bg: '#1f3d2d', border: '#2ecc71' }, '3': { bg: '#3d2d1f', border: '#e67e22' }, Broadway: { bg: '#3d1f1f', border: '#e74c3c' }, Presales: { bg: '#1f3d3a', border: '#1abc9c' }, Parking: { bg: '#2d2d1f', border: '#f1c40f' } };
                        const tc = tierColors[tier];
                        return (
                        <label
                          key={tier}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem',
                            cursor: 'pointer',
                            padding: '0.4rem 0.75rem',
                            borderRadius: '4px',
                            background: formData.assignedTiers.includes(tier)
                              ? tc.bg
                              : '#252538',
                            border: formData.assignedTiers.includes(tier)
                              ? '1px solid ' + tc.border
                              : '1px solid #3d3d5c'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formData.assignedTiers.includes(tier)}
                            onChange={() => {
                              setFormData(prev => ({
                                ...prev,
                                assignedTiers: prev.assignedTiers.includes(tier)
                                  ? prev.assignedTiers.filter(t => t !== tier)
                                  : [...prev.assignedTiers, tier]
                              }));
                            }}
                          />
                          <span className={`tier-badge tier-${tier.toLowerCase()}`} style={{ margin: 0 }}>
                            {tier === 'S' ? 'Tier S' : ['1','2','3'].includes(tier) ? `Tier ${tier}` : tier}
                          </span>
                        </label>
                      );
                      })}
                    </div>
                    <small style={{ color: '#a0a0a0', fontSize: '0.7rem', marginTop: '0.25rem', display: 'block' }}>
                      Pricer will see all performers assigned to selected tiers
                    </small>
                  </div>

                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      Date-Based Assignment
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={formData.dateAssignment.isActive}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            dateAssignment: { ...prev.dateAssignment, isActive: e.target.checked }
                          }))}
                        />
                        Enabled
                      </label>
                    </label>

                    {formData.dateAssignment.isActive && (
                      <div style={{ background: '#1a1a2e', padding: '1rem', borderRadius: '6px', border: '1px solid #3d3d5c' }}>
                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="dateMode"
                              value="rolling"
                              checked={formData.dateAssignment.mode === 'rolling'}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                dateAssignment: { ...prev.dateAssignment, mode: 'rolling' }
                              }))}
                            />
                            Rolling Window
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                            <input
                              type="radio"
                              name="dateMode"
                              value="custom"
                              checked={formData.dateAssignment.mode === 'custom'}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                dateAssignment: { ...prev.dateAssignment, mode: 'custom' }
                              }))}
                            />
                            Custom Range
                          </label>
                        </div>

                        {formData.dateAssignment.mode === 'rolling' ? (
                          <div>
                            <label className="form-label" style={{ fontSize: '0.8rem' }}>Show events in these windows:</label>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                              {DATE_WINDOWS.map(w => {
                                const checked = formData.dateAssignment.windows.includes(w.key);
                                return (
                                  <label key={w.key} style={{
                                    display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer',
                                    padding: '0.4rem 0.75rem', borderRadius: '4px',
                                    background: checked ? '#1a3d1f' : '#252538',
                                    border: checked ? '1px solid #27ae60' : '1px solid #3d3d5c'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => setFormData(prev => {
                                        const cur = prev.dateAssignment.windows;
                                        const windows = cur.includes(w.key)
                                          ? cur.filter(k => k !== w.key)
                                          : [...cur, w.key];
                                        return { ...prev, dateAssignment: { ...prev.dateAssignment, windows } };
                                      })}
                                    />
                                    <span>{w.label}</span>
                                    <span style={{ color: '#a0a0a0', fontSize: '0.7rem' }}>({w.hint})</span>
                                  </label>
                                );
                              })}
                            </div>
                            {formData.dateAssignment.windows.length === 0 && (
                              <small style={{ color: '#e67e22', fontSize: '0.7rem', marginTop: '0.25rem', display: 'block' }}>
                                Select at least one window
                              </small>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <label className="form-label" style={{ fontSize: '0.8rem' }}>Start Date</label>
                              <input
                                type="date"
                                className="form-input"
                                value={formData.dateAssignment.startDate || ''}
                                onChange={e => setFormData(prev => ({
                                  ...prev,
                                  dateAssignment: { ...prev.dateAssignment, startDate: e.target.value }
                                }))}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label className="form-label" style={{ fontSize: '0.8rem' }}>End Date</label>
                              <input
                                type="date"
                                className="form-input"
                                value={formData.dateAssignment.endDate || ''}
                                onChange={e => setFormData(prev => ({
                                  ...prev,
                                  dateAssignment: { ...prev.dateAssignment, endDate: e.target.value }
                                }))}
                              />
                            </div>
                          </div>
                        )}

                        {formData.dateAssignment.mode === 'custom' &&
                          (!formData.dateAssignment.startDate || !formData.dateAssignment.endDate ? (
                            <small style={{ color: '#e67e22', fontSize: '0.7rem', marginTop: '0.25rem', display: 'block' }}>
                              Select a start and end date
                            </small>
                          ) : formData.dateAssignment.startDate > formData.dateAssignment.endDate ? (
                            <small style={{ color: '#e67e22', fontSize: '0.7rem', marginTop: '0.25rem', display: 'block' }}>
                              Start date must be on or before the end date
                            </small>
                          ) : null)}

                        <small style={{ color: '#a0a0a0', fontSize: '0.7rem', marginTop: '0.5rem', display: 'block' }}>
                          Pricer will see ALL shows within this date window, in addition to their tier/artist assignments
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Assigned Artists ({formData.assignedArtists.length} selected)
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search artists..."
                      value={artistSearch}
                      onChange={e => setArtistSearch(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    {formData.assignedArtists.length > 0 && (
                      <div style={{ marginBottom: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {formData.assignedArtists.map(artist => (
                          <span
                            key={artist}
                            style={{
                              background: '#3b82f6',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            {artist}
                            <button
                              onClick={() => toggleArtist(artist)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '0 0.25rem',
                                fontSize: '1rem',
                                lineHeight: 1
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="artist-list">
                      {performers
                        .filter(p =>
                          artistSearch === '' ||
                          p.name.toLowerCase().includes(artistSearch.toLowerCase()) ||
                          (p.category && p.category.toLowerCase().includes(artistSearch.toLowerCase()))
                        )
                        .map(p => (
                        <div
                          key={p.id}
                          className={`artist-item ${formData.assignedArtists.includes(p.name) ? 'selected' : ''}`}
                          onClick={() => toggleArtist(p.name)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.assignedArtists.includes(p.name)}
                            onChange={() => toggleArtist(p.name)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          {p.name}
                          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#a0a0a0' }}>
                            {p.category}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="message-error" style={{ marginTop: '1rem' }}>
                  {error}
                </div>
              )}

              {editingUser && superAdmin && formData.role !== 'buyer' && (
                <SkyboxSessionPanel
                  userId={editingUser.id}
                  username={editingUser.username}
                  session={skyboxSessionsById[editingUser.id] || null}
                  credentialsEnabled={skyboxSessionsData?.credentialsEnabled !== false}
                  onMessage={flash}
                />
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button className="btn btn-primary" onClick={handleSaveUser} style={{ flex: 1 }}>
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Admin;
